import { Menu } from "@mojo/blueprint/Icon";
import useWindowSize from "@mojo/blueprint/utils/useWindowSize";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { useEffect, useState } from "react";
import Button from "../../Button";
import { Presence } from "../../Presence";
import { Logo } from "../../Template/Nav/Logo";
import { xlValue } from "../../Theme/breakpoints";
import useMeasure from "../../utils/useMeasure";
import {
  Container,
  LogoContainer,
  MenuContainer,
  MinContainerHeight,
  MinimalContainer,
  MyMojoButtonContainer,
  NavButtonContainer,
  NavButtons,
  NavDot,
  NavLabel,
} from "./styles/Sidenav.css";

interface NavButtonProps {
  path: string;
  label: string;
  isActive: boolean;
}

const NavButton = ({
  path,
  label,
  isActive,
  ...rest
}: NavButtonProps & React.HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      className={NavButtonContainer({ active: isActive })}
      onClick={() => (window.location.href = path)}
      data-to={path}
      {...rest}
    >
      <div className={NavDot({ active: isActive })} />
      <div className={NavLabel}>{label}</div>
    </button>
  );
};

type SideNavProps = {
  baseUrl: string;
};

/**
 * This component attempts to re-implement the side navigation from the legacy Mymojo app.
 * Parts of it have weird styles with uneven paddings and such, this is mostly because
 * the original styles were not very consistent.
 */
const Sidenav = ({ baseUrl }: SideNavProps) => {
  const [currentPath, setCurrentPath] = useState<string>(
    window.location.pathname,
  );

  // Update the currentPath when the location changes
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    // Listen for popstate events (browser back/forward buttons)
    window.addEventListener("popstate", handleLocationChange);

    // Some routing libraries might not trigger popstate
    // This is a common approach to detect route changes
    const originalPushState = window.history.pushState;
    window.history.pushState = function(...args) {
      const result = originalPushState.apply(this, args);
      window.dispatchEvent(new Event("locationchange"));
      return result;
    };

    window.addEventListener("locationchange", handleLocationChange);

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
      window.removeEventListener("locationchange", handleLocationChange);
      window.history.pushState = originalPushState;
    };
  }, []);

  const [open, setOpen] = useState(false);
  const [ref, { height }] = useMeasure();

  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    if (windowWidth >= xlValue) {
      setOpen(true);
    }
  }, [windowWidth]);

  return (
    <nav className={Container({ open: true })} data-baseurl={baseUrl}>
      <div className={MinimalContainer} ref={ref}>
        <div className={LogoContainer}>
          <Logo brand="Mojo" />
        </div>
        <div className={MenuContainer}>
          <button onClick={() => setOpen(!open)}>
            <Menu />
          </button>
        </div>
      </div>
      {/* We need this here because it hides the initial animation when we load the page */}
      <Presence present={open}>
        <div
          className={NavButtons}
          data-state={open ? "open" : "closed"}
          style={{
            ...assignInlineVars({
              [MinContainerHeight]: `${height}px`,
            }),
          }}
        >
          <div className={MyMojoButtonContainer}>
            <Button
              variant="secondary"
              style={{
                height: 56,
              }}
            >
              mymojo
            </Button>
          </div>
          <NavButton
            path={`${baseUrl}/status`}
            label="Status"
            isActive={currentPath.includes("/status")}
          />
          <NavButton
            path={`${baseUrl}/recommendation`}
            label="Recommendation"
            isActive={currentPath.includes("/recommendation")}
          />
          <NavButton
            path={`${baseUrl}/conveyancing`}
            label="Conveyancing"
            isActive={currentPath.includes("/conveyancing")}
          />
          <NavButton
            path={`${baseUrl}/documents`}
            label="Documents"
            isActive={currentPath.includes("/documents")}
          />
          <NavButton
            path={`${baseUrl}/property-report`}
            label="Property"
            isActive={currentPath.includes("/property-report")}
          />
        </div>
      </Presence>
    </nav>
  );
};

export default Sidenav;
